import React, { useEffect, useState } from "react";
import { Spin, Switch, Upload } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { saveSettings, updateSettings, updateValue } from "../../apis/setting";
import { useNotification } from "../../NotificationContext";
import { Setting } from "../../interfaces/Setting";

type HeaderMode = "no_header" | "default_header" | "custom_header_image";

interface PrescriptionHeaderSettingsProps {
  settings: Setting[];
  onRefresh: () => void;
}

const MARGIN_OPTIONS = [
  { label: "30 mm", value: "30" },
  { label: "40 mm", value: "40" },
  { label: "50 mm", value: "50" },
  { label: "60 mm", value: "60" },
];

function toAbsoluteUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/${url.replace(/^\//, "")}`;
}

const PrescriptionHeaderSettings: React.FC<PrescriptionHeaderSettingsProps> = ({
  settings,
  onRefresh,
}) => {
  const { showNotification } = useNotification();

  const headerSetting = settings.find((s) => s.code === "prescription_header");
  const logoSetting = settings.find((s) => s.code === "prescription_logo");
  const logoSizeSetting = settings.find((s) => s.code === "prescription_logo_size");
  const marginSetting = settings.find(
    (s) => s.code === "prescription_no_header_margin"
  );
  const customImageSetting = settings.find(
    (s) => s.code === "custom_header_image"
  );
  const showClinicNameSetting = settings.find(
    (s) => s.code === "prescription_show_clinic_name"
  );
  const showAddressSetting = settings.find(
    (s) => s.code === "prescription_show_address"
  );
  const showPhoneSetting = settings.find(
    (s) => s.code === "prescription_show_phone"
  );
  const clinicRegSetting = settings.find(
    (s) => s.code === "clinic_registration_number"
  );

  const parseLogoSize = (val?: string) => {
    const [w, h] = (val || "40x20").split("x").map(Number);
    return { w: w || 40, h: h || 20 };
  };

  const [mode, setMode] = useState<HeaderMode>(
    (headerSetting?.value as HeaderMode) || "default_header"
  );
  const [margin, setMargin] = useState(marginSetting?.value || "40");
  const [logoUrl, setLogoUrl] = useState<string | null>(
    logoSetting?.value ? toAbsoluteUrl(logoSetting.value) : null
  );
  const [logoWidthMm, setLogoWidthMm] = useState(() => parseLogoSize(logoSizeSetting?.value).w);
  const [logoHeightMm, setLogoHeightMm] = useState(() => parseLogoSize(logoSizeSetting?.value).h);
  const [logoSizeSaving, setLogoSizeSaving] = useState(false);
  const [customImageUrl, setCustomImageUrl] = useState<string | null>(
    customImageSetting?.value
      ? toAbsoluteUrl(customImageSetting.value)
      : null
  );
  const [logoUploading, setLogoUploading] = useState(false);
  const [customImageUploading, setCustomImageUploading] = useState(false);
  const [customImageError, setCustomImageError] = useState<string | null>(null);
  const [marginSaving, setMarginSaving] = useState(false);
  const [modeSaving, setModeSaving] = useState(false);
  const [showClinicName, setShowClinicName] = useState(
    showClinicNameSetting ? showClinicNameSetting.value !== "false" : true
  );
  const [showClinicNameSaving, setShowClinicNameSaving] = useState(false);
  const [showAddress, setShowAddress] = useState(
    showAddressSetting ? showAddressSetting.value !== "false" : true
  );
  const [showAddressSaving, setShowAddressSaving] = useState(false);
  const [showPhone, setShowPhone] = useState(
    showPhoneSetting ? showPhoneSetting.value !== "false" : true
  );
  const [showPhoneSaving, setShowPhoneSaving] = useState(false);
  const [clinicRegNumber, setClinicRegNumber] = useState(clinicRegSetting?.value || "");
  const [clinicRegSaving, setClinicRegSaving] = useState(false);

  useEffect(() => {
    const incoming = (headerSetting?.value as HeaderMode) || "default_header";
    setMode(incoming);
  }, [headerSetting?.value]);

  useEffect(() => {
    setMargin(marginSetting?.value || "40");
  }, [marginSetting?.value]);

  useEffect(() => {
    setLogoUrl(
      logoSetting?.value ? toAbsoluteUrl(logoSetting.value) : null
    );
  }, [logoSetting?.value]);

  useEffect(() => {
    setCustomImageUrl(
      customImageSetting?.value
        ? toAbsoluteUrl(customImageSetting.value)
        : null
    );
  }, [customImageSetting?.value]);

  useEffect(() => {
    setShowClinicName(
      showClinicNameSetting ? showClinicNameSetting.value !== "false" : true
    );
  }, [showClinicNameSetting?.value]);

  useEffect(() => {
    setShowAddress(
      showAddressSetting ? showAddressSetting.value !== "false" : true
    );
  }, [showAddressSetting?.value]);

  useEffect(() => {
    setShowPhone(
      showPhoneSetting ? showPhoneSetting.value !== "false" : true
    );
  }, [showPhoneSetting?.value]);

  useEffect(() => {
    setClinicRegNumber(clinicRegSetting?.value || "");
  }, [clinicRegSetting?.value]);

  const saveClinicRegNumber = async () => {
    setClinicRegSaving(true);
    try {
      if (clinicRegSetting?.id) {
        await updateSettings({ ...clinicRegSetting, value: clinicRegNumber });
      } else {
        await saveSettings({
          code: "clinic_registration_number",
          name: "clinic_registration_number",
          value: clinicRegNumber || "pending",
        });
      }
      onRefresh();
      showNotification("success", "Registration number saved.", "", "topRight");
    } catch {
      showNotification("error", "Failed to save.", "", "topRight");
    } finally {
      setClinicRegSaving(false);
    }
  };

  const saveMode = async (newMode: HeaderMode) => {
    setModeSaving(true);
    try {
      if (headerSetting?.id) {
        await updateSettings({ ...headerSetting, value: newMode });
      } else {
        await saveSettings({
          code: "prescription_header",
          name: "Prescription Header",
          value: newMode,
        });
      }
      window.localStorage.setItem("prescriptionHeader", newMode);
      onRefresh();
    } catch {
      showNotification("error", "Failed to save header mode.", "", "topRight");
    } finally {
      setModeSaving(false);
    }
  };

  const handleModeSelect = (newMode: HeaderMode) => {
    setMode(newMode);
    saveMode(newMode);
  };

  const handleMarginChange = async (value: string) => {
    setMargin(value);
    setMarginSaving(true);
    try {
      if (marginSetting?.id) {
        await updateSettings({ ...marginSetting, value });
      } else {
        await saveSettings({
          code: "prescription_no_header_margin",
          name: "Prescription No Header Margin",
          value,
        });
      }
      onRefresh();
    } catch {
      showNotification("error", "Failed to save margin.", "", "topRight");
    } finally {
      setMarginSaving(false);
    }
  };

  const handleLogoUpload = async (opts: any) => {
    let settingId = logoSetting?.id;
    setLogoUploading(true);
    try {
      if (!settingId) {
        const res: any = await saveSettings({
          code: "prescription_logo",
          name: "Prescription Logo",
          value: "pending",
        });
        settingId = res?.data?.data?.id || res?.data?.id;
        if (!settingId) throw new Error("Could not create logo setting.");
      }
      const res: any = await updateValue({ id: settingId, value: opts.file });
      const newUrl = res?.data?.data?.value || res?.data?.value;
      if (newUrl) {
        const absolute = toAbsoluteUrl(newUrl);
        setLogoUrl(absolute);
        window.localStorage.setItem("prescriptionLogo", absolute);
        onRefresh();
      }
      showNotification("success", "Logo uploaded.", "", "topRight");
      opts.onSuccess?.({});
    } catch {
      showNotification("error", "Logo upload failed.", "", "topRight");
      opts.onError?.({});
    } finally {
      setLogoUploading(false);
    }
  };

  const saveLogoSize = async () => {
    setLogoSizeSaving(true);
    const value = `${logoWidthMm}x${logoHeightMm}`;
    try {
      if (logoSizeSetting?.id) {
        await updateSettings({ ...logoSizeSetting, value });
      } else {
        await saveSettings({
          code: "prescription_logo_size",
          name: "Prescription Logo Size",
          value,
        });
      }
      onRefresh();
      showNotification("success", "Logo size saved.", "", "topRight");
    } catch {
      showNotification("error", "Failed to save logo size.", "", "topRight");
    } finally {
      setLogoSizeSaving(false);
    }
  };

  const handleShowClinicNameToggle = async (checked: boolean) => {
    setShowClinicName(checked);
    setShowClinicNameSaving(true);
    try {
      if (showClinicNameSetting?.id) {
        await updateSettings({ ...showClinicNameSetting, value: String(checked) });
      } else {
        await saveSettings({
          code: "prescription_show_clinic_name",
          name: "prescription_show_clinic_name",
          value: String(checked),
        });
      }
      onRefresh();
    } catch {
      showNotification("error", "Failed to save.", "", "topRight");
    } finally {
      setShowClinicNameSaving(false);
    }
  };

  const handleShowAddressToggle = async (checked: boolean) => {
    setShowAddress(checked);
    setShowAddressSaving(true);
    try {
      if (showAddressSetting?.id) {
        await updateSettings({ ...showAddressSetting, value: String(checked) });
      } else {
        await saveSettings({
          code: "prescription_show_address",
          name: "prescription_show_address",
          value: String(checked),
        });
      }
      onRefresh();
    } catch {
      showNotification("error", "Failed to save.", "", "topRight");
    } finally {
      setShowAddressSaving(false);
    }
  };

  const handleShowPhoneToggle = async (checked: boolean) => {
    setShowPhone(checked);
    setShowPhoneSaving(true);
    try {
      if (showPhoneSetting?.id) {
        await updateSettings({ ...showPhoneSetting, value: String(checked) });
      } else {
        await saveSettings({
          code: "prescription_show_phone",
          name: "prescription_show_phone",
          value: String(checked),
        });
      }
      onRefresh();
    } catch {
      showNotification("error", "Failed to save.", "", "topRight");
    } finally {
      setShowPhoneSaving(false);
    }
  };

  const handleCustomImageUpload = async (opts: any) => {
    setCustomImageError(null);
    const file: File = opts.file;
    const isPNG = file.type === "image/png";
    const isJPG = file.type === "image/jpeg";
    if (!isPNG && !isJPG) {
      setCustomImageError("Only JPG or PNG files are allowed.");
      opts.onError?.({});
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
    const aspectRatio = img.width / img.height;
    URL.revokeObjectURL(url);
    if (Math.abs(aspectRatio - 4) > 0.1) {
      setCustomImageError("Required aspect ratio is 4:1.");
      opts.onError?.({});
      return;
    }

    let settingId = customImageSetting?.id;
    setCustomImageUploading(true);
    try {
      if (!settingId) {
        const res: any = await saveSettings({
          code: "custom_header_image",
          name: "Custom Header Image",
          value: "pending",
        });
        settingId = res?.data?.data?.id || res?.data?.id;
        if (!settingId) throw new Error("Could not create setting.");
      }
      const res: any = await updateValue({ id: settingId, value: file });
      const newUrl = res?.data?.data?.value || res?.data?.value;
      if (newUrl) {
        const absolute = toAbsoluteUrl(newUrl);
        setCustomImageUrl(absolute);
        onRefresh();
      }
      showNotification("success", "Header image uploaded.", "", "topRight");
      opts.onSuccess?.({});
    } catch {
      showNotification("error", "Upload failed.", "", "topRight");
      opts.onError?.({});
    } finally {
      setCustomImageUploading(false);
    }
  };

  const user = (() => {
    try {
      return JSON.parse(window.localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const orgName =
    settings.find((s) => s.code === "organization_name")?.value ||
    user?.activeClinic?.name ||
    "Clinic Name";
  const address =
    settings.find((s) => s.code === "address")?.value ||
    user?.activeClinic?.address ||
    "";
  const phone =
    settings.find((s) => s.code === "phone")?.value ||
    user?.activeClinic?.phone ||
    "";
  const cards: {
    id: HeaderMode;
    title: string;
    description: string;
  }[] = [
    {
      id: "no_header",
      title: "Pre-printed Letterhead",
      description:
        "We'll leave a blank margin at the top. Use this if your paper already has a printed letterhead.",
    },
    {
      id: "default_header",
      title: "Auto-generated Header",
      description:
        "The app generates a header using your clinic name, address, phone and the treating doctor's details.",
    },
    {
      id: "custom_header_image",
      title: "Custom Header Image",
      description: "Upload a full-width header image (4:1 aspect ratio).",
    },
  ];

  return (
    <div className="space-y-4 max-w-2xl">
      {modeSaving && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spin size="small" />
          <span>Saving...</span>
        </div>
      )}

      {cards.map((card) => {
        const selected = mode === card.id;
        return (
          <div
            key={card.id}
            onClick={() => handleModeSelect(card.id)}
            className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
              selected
                ? "border-indigo-600 bg-indigo-50"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                readOnly
                checked={selected}
                className="mt-0.5 text-indigo-600 focus:ring-indigo-600"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {card.title}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {card.description}
                </p>

                {selected && card.id === "no_header" && (
                  <div
                    className="mt-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <label className="text-sm font-medium text-gray-700">
                        Blank margin at top:
                      </label>
                      <div className="flex gap-2">
                        {MARGIN_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => handleMarginChange(opt.value)}
                            disabled={marginSaving}
                            className={`px-3 py-1 rounded-md text-sm font-medium border transition-colors disabled:opacity-50 ${
                              margin === opt.value
                                ? "bg-indigo-600 text-white border-indigo-600"
                                : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {marginSaving && <Spin size="small" />}
                    </div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden w-48">
                      <div
                        className="bg-gray-100"
                        style={{ height: `${Number(margin) * 0.5}px` }}
                      />
                      <div className="p-3 space-y-1.5">
                        {[70, 90, 60, 80, 50].map((w, i) => (
                          <div
                            key={i}
                            className="bg-gray-200 rounded"
                            style={{ height: 6, width: `${w}%` }}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Mini-preview — dashed area represents the blank margin
                    </p>
                  </div>
                )}

                {selected && card.id === "default_header" && (
                  <div
                    className="mt-4 space-y-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Clinic Logo (optional)
                      </p>
                      <div className="flex items-start gap-4">
                        {logoUrl && (
                          <div className="border rounded p-2 bg-gray-50">
                            <img
                              src={logoUrl}
                              alt="Clinic logo"
                              className="max-h-12 max-w-32 object-contain"
                            />
                            <p className="text-xs text-gray-400 mt-1 text-center">
                              Current logo
                            </p>
                          </div>
                        )}
                        <div>
                          <Upload
                            name="file"
                            listType="picture-card"
                            showUploadList={false}
                            accept="image/png,image/jpeg"
                            customRequest={handleLogoUpload}
                          >
                            <div className="p-3 text-center cursor-pointer">
                              {logoUploading ? (
                                <LoadingOutlined />
                              ) : (
                                <PlusOutlined />
                              )}
                              <div className="text-xs mt-1">
                                {logoUrl ? "Replace" : "Upload"}
                              </div>
                            </div>
                          </Upload>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG or JPG
                          </p>
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-600 font-medium">Size:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={5}
                                max={200}
                                value={logoWidthMm}
                                onChange={(e) => setLogoWidthMm(Number(e.target.value))}
                                className="w-16 rounded border border-gray-300 px-2 py-0.5 text-sm text-center"
                              />
                              <span className="text-xs text-gray-500">mm W</span>
                            </div>
                            <span className="text-xs text-gray-400">×</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min={5}
                                max={100}
                                value={logoHeightMm}
                                onChange={(e) => setLogoHeightMm(Number(e.target.value))}
                                className="w-16 rounded border border-gray-300 px-2 py-0.5 text-sm text-center"
                              />
                              <span className="text-xs text-gray-500">mm H</span>
                            </div>
                            <button
                              onClick={saveLogoSize}
                              disabled={logoSizeSaving}
                              className="px-2 py-0.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {logoSizeSaving ? "Saving…" : "Save"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={showClinicName}
                          onChange={handleShowClinicNameToggle}
                          loading={showClinicNameSaving}
                          size="small"
                        />
                        <span className="text-sm text-gray-700">Show clinic name</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={showAddress}
                          onChange={handleShowAddressToggle}
                          loading={showAddressSaving}
                          size="small"
                        />
                        <span className="text-sm text-gray-700">Show address</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={showPhone}
                          onChange={handleShowPhoneToggle}
                          loading={showPhoneSaving}
                          size="small"
                        />
                        <span className="text-sm text-gray-700">Show phone number</span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Clinic / Hospital Registration Number
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="block rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                          style={{ minWidth: 240 }}
                          placeholder="e.g. MH-12345"
                          value={clinicRegNumber}
                          onChange={(e) => setClinicRegNumber(e.target.value)}
                        />
                        <button
                          onClick={saveClinicRegNumber}
                          disabled={clinicRegSaving}
                          className="px-3 py-1.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {clinicRegSaving ? "Saving…" : "Save"}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Shown below the phone number in the prescription header
                      </p>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <div className="border-b-2 border-gray-200 px-4 py-3 bg-white flex justify-between items-center">
                        <div>
                          {logoUrl && (
                            <img
                              src={logoUrl}
                              alt="Logo"
                              style={{
                                width: logoWidthMm + "mm",
                                height: logoHeightMm + "mm",
                                objectFit: "contain",
                                display: "block",
                                marginBottom: (showClinicName || showAddress || showPhone) ? 4 : 0,
                              }}
                            />
                          )}
                          {showClinicName && (
                            <div className="font-bold text-base">{orgName}</div>
                          )}
                          {showAddress && address && (
                            <div className="text-xs text-gray-500">{address}</div>
                          )}
                          {showPhone && phone && (
                            <div className="text-xs text-gray-500">☎ {phone}</div>
                          )}
                        </div>
                        {clinicRegNumber && (
                          <div className="text-xs text-gray-500 text-right">
                            Reg. No. {clinicRegNumber}
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-2 space-y-1.5">
                        {[65, 85, 50, 75].map((w, i) => (
                          <div
                            key={i}
                            className="bg-gray-100 rounded"
                            style={{ height: 7, width: `${w}%` }}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      Live preview — uses your actual clinic data
                    </p>
                  </div>
                )}

                {selected && card.id === "custom_header_image" && (
                  <div
                    className="mt-4 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {customImageUrl && (
                      <div className="border rounded overflow-hidden">
                        <img
                          src={customImageUrl}
                          alt="Custom header"
                          className="w-full"
                          style={{ maxHeight: 120, objectFit: "contain" }}
                        />
                      </div>
                    )}
                    <div>
                      <Upload
                        name="file"
                        listType="picture-card"
                        showUploadList={false}
                        accept="image/png,image/jpeg"
                        customRequest={handleCustomImageUpload}
                      >
                        <div className="p-4 text-center cursor-pointer">
                          {customImageUploading ? (
                            <LoadingOutlined />
                          ) : (
                            <PlusOutlined />
                          )}
                          <div className="text-xs mt-1" style={{ width: 100 }}>
                            {customImageUrl ? "Replace image" : "Upload image"}
                          </div>
                        </div>
                      </Upload>
                      {customImageError && (
                        <p className="mt-2 text-sm text-red-600">
                          {customImageError}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        <span className="text-red-500">*</span> JPG or PNG only
                      </p>
                      <p className="text-xs text-gray-500">
                        <span className="text-red-500">*</span> Required ratio
                        4:1 (e.g. 800 × 200 px)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrescriptionHeaderSettings;
